import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Button, Col, Input, Row, Spin, Typography, Card, message } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';

import DeviceSelect from '../../components/DeviceSelect';
import { User } from '../../models/User';
import { Zond } from '../../models/Zond';

const { Title, Paragraph } = Typography;

interface Props {
    user: User;
    zonds: Zond[];
}

const AssistantPage = (props: Props) => {
    const [selectedZond, setSelectedZond] = useState('');
    const [culture, setCulture] = useState('Пшеница');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<string | null>(null);

    const handleZondChange = (zond: string) => {
        setSelectedZond(zond);
        setResult(null);
    };

    const handleCultureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCulture(e.target.value);
        setResult(null);
    };

    const handleAnalyze = async () => {
        if (!selectedZond) {
            message.warning('Пожалуйста, выберите станцию');
            return;
        }
        if (!culture.trim()) {
            message.warning('Введите название культуры');
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const timezoneOffset = new Date().getTimezoneOffset();

            const response = await fetch('/assistant/analyze', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    stationID: selectedZond,
                    culture: culture.trim(),
                    timezoneOffset: timezoneOffset,
                }),
            });

            // Пытаемся распарсить JSON из ответа, даже если статус не OK
            let data: any = null;
            try {
                data = await response.json();
            } catch (e) {
                // Игнорируем, если не JSON
            }

            if (data && data.status === false && data.message) {
                // Сервер вернул понятную ошибку
                setResult('Ошибка: ' + data.message);
            } else if (response.ok && data && data.status === true && data.summary) {
                setResult(data.summary);
            } else {
                // Непонятная ошибка
                setResult('Ошибка: ' + (data?.message || 'Не удалось получить аналитику'));
            }
        } catch (error: any) {
            // Ошибка сети или другие исключения
            setResult('Ошибка: ' + (error.message || 'Произошла ошибка при запросе'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Title level={3}>Агро-помощник</Title>
            <Paragraph>
                Анализирует данные с выбранной станции: погодные условия, влажность и температуру почвы, учитывая текущую культуру. Помогает оценить состояние посевов и выявить возможные риски.
            </Paragraph>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={8}>
                    <DeviceSelect
                        user={props.user}
                        zonds={props.zonds}
                        handleChangeZond={handleZondChange}
                    />
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Input
                        placeholder="Культура (например, Пшеница)"
                        value={culture}
                        onChange={handleCultureChange}
                        size="large"
                    />
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<RobotOutlined />}
                        onClick={handleAnalyze}
                        loading={loading}
                        disabled={!selectedZond}
                        block
                    >
                        Получить аналитику
                    </Button>
                </Col>
            </Row>

            {loading && (
                <div style={{ textAlign: 'center', margin: 40 }}>
                    <Spin size="large" tip="Анализируем данные..." />
                </div>
            )}

            {result && !loading && (
                <Card style={{ marginTop: 24 }}>
                    <Title level={4}>Заключение</Title>
                    <div style={{ fontSize: '16px' }}>
                        <ReactMarkdown>{result}</ReactMarkdown>
                    </div>
                </Card>
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => {
    return {
        user: state.user.user,
        zonds: state.zonds.zonds,
    };
};

export default connect(mapStateToProps)(AssistantPage);